import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import typewriterImage from '@/assets/typewriter-harmony.jpg';

const HarmonyNotRejection = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Harmony, Not Rejection: How Mystic Forest Sees Technology and AI"
        description="Technology is not good or bad; nor is it neutral. Exploring Mystic Forest's pro-human philosophy on digital minimalism, AI, and the gentle nostalgia for proportionate living."
        keywords={["technology philosophy", "digital minimalism", "AI ethics", "mindful technology", "digital detox", "surveillance capitalism", "intentional living"]}
        ogImage="/lovable-uploads/typewriter-harmony.jpg"
        canonicalUrl="https://mysticforest.club/blog/harmony-not-rejection"
        ogType="article"
        article={{
          publishedTime: "2025-11-01T00:00:00Z",
          author: "Piotr",
          section: "Philosophy",
          tags: ["Technology", "AI", "Digital Minimalism", "Mindfulness"]
        }}
      />
      <Navigation />
      
      <section className="relative pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/blog')}
            className="mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Digital Minimalism</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Harmony, Not Rejection: How Mystic Forest Sees Technology and AI
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>November 1, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>11 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src={typewriterImage} 
              alt="Vintage typewriter representing mindful, intentional creation and the harmony between technology and human creativity"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
            <p className="absolute bottom-4 right-4 text-xs text-white/90 bg-black/40 px-3 py-1.5 rounded backdrop-blur-sm">
              Photo: Alberto Constante/Pinterest
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic border-l-2 border-primary/30 pl-6">
              "Technology is not good or bad; nor is it neutral."<br />
              <span className="text-sm">— Melvin Kranzberg, Six Laws of Technology (Technology and Culture, 1986, p. 545)</span>
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">A Misunderstood Philosophy</h2>
              
              <p>
                Mystic Forest is often seen as a refuge from the digital world — a place that values silence, simplicity, and real presence. And yet, we are not opposed to technology.<br />
                We use it every day, consciously and gratefully.
              </p>

              <p>
                Our philosophy is not anti-digital — it's <strong className="text-accent">pro-human</strong>.
              </p>

              <p>
                We are nostalgic, yes — but not for the lack of Wi-Fi or smartphones.<br />
                We long for something deeper: the texture of living that existed before we outsourced attention to algorithms.<br />
                The kind of life where time unfolded slowly, where conversation had weight, and where being unreachable was not a flaw, but a state of grace.
              </p>

              <p>
                That nostalgia isn't about rejecting progress.<br />
                It's about remembering proportion — a time when technology was a tool, not the air we breathe.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Technology as a Tool, Not a Tyrant</h2>

              <p>
                Cal Newport, in <em>Digital Minimalism</em>, writes that technology should be used intentionally, not impulsively:
              </p>

              <div className="text-xl leading-relaxed text-muted-foreground my-8 font-light italic border-l-2 border-primary/30 pl-6">
                "Digital minimalists see new technologies as tools to be used to support things they deeply value — not as sources of value themselves."<br />
                <span className="text-sm">(Digital Minimalism: Choosing a Focused Life in a Noisy World, Cal Newport, Penguin, 2019, p. 28)</span>
              </div>

              <p>
                At Mystic Forest, we hold this same belief.<br />
                We are not trying to run away from technology — we're learning how to make peace with it.
              </p>

              <p>
                Technology is remarkable: it can connect, educate, heal, and inspire.<br />
                But only when we are awake to its influence.<br />
                The danger isn't in the tool — it's in forgetting that it's a tool.
              </p>

              <p>
                Our retreats, workshops, and reflections are not about erasing the digital world. They are about creating the inner silence necessary to return to it wisely.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">AI as Reflection, Not Replacement</h2>

              <p>
                Artificial Intelligence is a mirror — it amplifies whatever we bring to it: curiosity or distraction, creativity or noise.<br />
                Used ethically, it can support education, accessibility, and art.<br />
                Used carelessly, it can deepen the very disconnection we're trying to heal.
              </p>

              <p>
                Philosopher Shoshana Zuboff describes this risk clearly:
              </p>

              <div className="text-xl leading-relaxed text-muted-foreground my-8 font-light italic border-l-2 border-primary/30 pl-6">
                "Surveillance capitalism unilaterally claims human experience as free raw material for translation into behavioral data."<br />
                <span className="text-sm">(The Age of Surveillance Capitalism, Profile Books, 2019, p. 11)</span>
              </div>

              <p>
                That is what we resist — not AI itself, but the idea that our inner life is something to be harvested and optimized.
              </p>

              <p>
                In truth, this article itself is co-created with AI — and that's no contradiction.<br />
                It shows that when used thoughtfully, technology can serve mindfulness, not destroy it.<br />
                It can help articulate meaning, not replace it.
              </p>

              <p>
                AI, like nature, is neutral — it's our intention that determines the outcome.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Gentle Nostalgia</h2>

              <p>
                There's a quiet ache that runs through the modern world — a longing for a time when life was slower, messier, and more real.<br />
                A time when photos were kept in albums, not in clouds; when weekends belonged to families, not inboxes.
              </p>

              <p>
                We remember it not because it was perfect, but because it was proportionate.
              </p>

              <p>
                We didn't have to document our joy for it to feel real.<br />
                We didn't multitask our way through conversations.<br />
                We didn't need to "detox," because the world itself was gentler on our senses.
              </p>

              <div className="text-xl leading-relaxed text-muted-foreground my-8 font-light italic border-l-2 border-primary/30 pl-6">
                "Technology gives us everything we want, except time."<br />
                <span className="text-sm">— John Zerzan, Elements of Refusal, 1999, p. 84</span>
              </div>

              <p>
                At Mystic Forest, this nostalgia becomes a form of awareness — not an escape into the past, but a reminder of what to preserve as we move forward.
              </p>

              <p>
                We don't wish to abandon the digital age.<br />
                We wish to humanize it — to teach coexistence instead of dependence.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Reclaiming Agency</h2>

              <p>
                The average person checks their phone 144 times a day (Asurion, 2022).<br />
                We do it not because we need to — but because we've forgotten what to do with quiet.
              </p>

              <p>
                Mystic Forest was born out of that recognition:<br />
                the realization that silence is not an absence, but a presence — a place where focus, intuition, and creativity live.
              </p>

              <p>
                Our philosophy doesn't condemn the screen.<br />
                It simply asks: what are you giving up for it?<br />
                And how can you reclaim it — one breath, one pause, one moment at a time?
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Integrating the Digital and the Natural</h2>

              <p>
                Philosopher Albert Borgmann once described the "device paradigm": the modern habit of letting technology mediate every experience (<em>Technology and the Character of Contemporary Life</em>, University of Chicago Press, 1984, p. 41).
              </p>

              <p>
                The solution, he said, isn't abandonment — it's re-engagement with reality.
              </p>

              <p>
                At Mystic Forest, that's exactly what we practice.<br />
                We use technology to connect people — and then invite them offline to rediscover touch, voice, movement, silence.<br />
                We let the digital serve the analog.<br />
                We let information lead to embodiment.
              </p>

              <p>
                Technology becomes not the end, but the bridge — between knowing and feeling, between idea and experience.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Future We Choose</h2>

              <p>
                The next evolution of mindfulness isn't about leaving technology behind — it's about living with it wisely.<br />
                Learning how to unplug with purpose, and to plug back in with intention.
              </p>

              <div className="text-xl leading-relaxed text-muted-foreground my-8 font-light italic border-l-2 border-primary/30 pl-6">
                "Technology is a great servant but a terrible master."<br />
                <span className="text-sm">— Sir John Culkin, paraphrasing Marshall McLuhan, The Medium is the Massage (1967, p. 26)</span>
              </div>

              <p>
                We don't dream of a world without technology.<br />
                We dream of one where technology coexists with silence, creativity, and empathy — where innovation grows alongside self-awareness.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">In the End</h2>

              <p>
                Mystic Forest doesn't preach resistance.<br />
                We practice discernment.
              </p>

              <p>
                We're not running away from progress — we're walking toward balance.<br />
                We believe that the future belongs not to those who are always connected, but to those who know when to disconnect.
              </p>

              <p>
                We are not anti-technology.<br />
                We are pro-human — nostalgic for the beauty of simplicity,<br />
                but committed to bringing that spirit into the modern age. 🌲
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h3 className="text-2xl font-serif font-light mb-6 mt-12 text-foreground">References</h3>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>Cal Newport, <em>Digital Minimalism: Choosing a Focused Life in a Noisy World</em>. Penguin Books, 2019, p. 28.</li>
                <li>Melvin Kranzberg, "Six Laws of Technology," <em>Technology and Culture</em>, Vol. 27, No. 3 (1986), p. 545.</li>
                <li>Shoshana Zuboff, <em>The Age of Surveillance Capitalism</em>. Profile Books, 2019, p. 11.</li>
                <li>Albert Borgmann, <em>Technology and the Character of Contemporary Life</em>. University of Chicago Press, 1984, p. 41.</li>
                <li>John Zerzan, <em>Elements of Refusal</em>. Columbia Alternative Press, 1999, p. 84.</li>
                <li>Sir John Culkin, <em>The Medium is the Massage</em>. 1967, p. 26.</li>
                <li>Asurion, "How Many Times Do People Check Their Phones?" (2022).</li>
              </ul>
            </div>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default HarmonyNotRejection;